import { useState, createContext, useContext, useEffect } from 'react'
import { PrinterPurpose } from '@truepill/tpos-types'
import type { ChildProps, Printer } from 'types'
import { useTPCacheContext } from './TPCacheProvider'

interface PrintProviderProps {
  printingInProgress: boolean
  selectedPrinter?: Printer
  setPrinter: (printer?: Printer, printerPurpose?: PrinterPurpose) => void
  getSavedPrinterId: (printerPurpose: PrinterPurpose) => string | null
  resetSavedPrinters: () => void
}

const PrintContext = createContext<PrintProviderProps>({
  getSavedPrinterId: () => {
    return ''
  },
  setPrinter: () => {
    return
  },
  resetSavedPrinters: () => {
    return
  },
  printingInProgress: false,
})

const PrintProvider = ({ children }: ChildProps): JSX.Element => {
  const [printer, setPrinter] = useState<Printer>()
  const prefixKey = 'selectedPrinterID'

  const value = {
    selectedPrinter: printer,
    getSavedPrinterId: (printerPurpose: PrinterPurpose) => {
      return localStorage.getItem(`selectedPrinterID-${printerPurpose}`)
    },
    setPrinter: (printer?: Printer, printerPurpose?: PrinterPurpose) => {
      setPrinter(printer)

      if (printer && printerPurpose) {
        const persistValue = printer ? printer._id : ''
        localStorage.setItem(`${prefixKey}-${printerPurpose}`, persistValue)
      } else if (printerPurpose) {
        localStorage.removeItem(`${prefixKey}-${printerPurpose}`)
      }
    },
    resetSavedPrinters: () => {
      Object.values(PrinterPurpose).map(value => localStorage.removeItem(`${prefixKey}-${value}`))
    },
    printingInProgress: true,
  }

  return <PrintContext.Provider value={value}>{children}</PrintContext.Provider>
}

const usePrintProvider = (printerPurpose?: PrinterPurpose): PrintProviderProps => {
  const printContext = useContext(PrintContext)
  const { getPrinterById } = useTPCacheContext()
  const { getSavedPrinterId, setPrinter } = printContext

  if (printContext === undefined) {
    throw new Error('Attempting to read PrintContext outside a Provider hierarchy')
  }

  useEffect(() => {
    if (!printerPurpose) return
    const persistedPrinterId = getSavedPrinterId(printerPurpose)
    if (persistedPrinterId && persistedPrinterId.length) {
      const printer = getPrinterById(persistedPrinterId)
      if (printer) {
        setPrinter(printer as Printer, printerPurpose)
      }
    }
  }, [printerPurpose])

  return printContext as PrintProviderProps
}

export type { PrintProviderProps }
export { PrintContext, usePrintProvider }
export default PrintProvider
